'use client';

import { c as _c } from "react/compiler-runtime";
import { useLayoutContext } from '@/context/LayoutContext';
import { usePartnerApply } from '@/hooks/usePartnerApply';
import { motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useIsClient, useMediaQuery } from 'usehooks-ts';
import { Container as HeroContentContainer, Container as HeroSectionContainer } from '@/app/[locale]/(create)/partner/components/Container';
import HeroCalculator from '@/app/[locale]/(create)/partner/components/HeroCalculator';
import TrustStrip from '@/app/[locale]/(create)/partner/components/TrustStrip';
import ViverseButton from '@/components/ViverseButton';
import HeroSectionBackground from '@/public/images/partner/hero-section-background.png';
import HeroSectionZack from '@/public/images/partner/hero-section-zack.png';
function LeftSideHeroContent() {
  const $ = _c(35);
  const t = useTranslations();
  const {
    handleClickApplyButton
  } = usePartnerApply();
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      opacity: 0,
      x: -28
    };
    t1 = {
      opacity: 1,
      x: 0
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      duration: 1.4,
      ease: [0.22, 1, 0.36, 1]
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t) {
    t3 = t("partnership.hero-section.title");
    $[3] = t;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = <span className="inline-block">{t3}</span>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t) {
    t5 = t("partnership.hero-section.subtitle-part1");
    $[7] = t;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t) {
    t6 = t("partnership.hero-section.subtitle-part2");
    $[9] = t;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t5 || $[12] !== t6) {
    t7 = <span className="gradient-title-2-reverse inline-block">{t5}{t6}</span>;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t) {
    t8 = t("partnership.hero-section.subtitle-part3");
    $[14] = t;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t8) {
    t9 = <span className="inline-block">{t8}</span>;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t4 || $[19] !== t7 || $[20] !== t9) {
    t10 = <h1 className="space-y-2 text-center text-[32px] leading-10 font-extrabold tracking-[0.4px] md:text-[56px] md:leading-16 lg:text-left">{t4}{t7}{t9}</h1>;
    $[18] = t4;
    $[19] = t7;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t) {
    t11 = t("partnership.hero-section.description");
    $[22] = t;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t11) {
    t12 = <p className="text-vive-primary-20 text-center text-base leading-[26px] tracking-[0.25px] md:text-lg md:leading-8 lg:text-left">{t11}</p>;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t) {
    t13 = t("partnership.hero-section.cta");
    $[26] = t;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== handleClickApplyButton || $[29] !== t13) {
    t14 = <div><ViverseButton size="medium" variant="colorful" className="min-h-12 text-[18px] whitespace-normal" onClick={handleClickApplyButton}>{t13}</ViverseButton></div>;
    $[28] = handleClickApplyButton;
    $[29] = t13;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t10 || $[32] !== t12 || $[33] !== t14) {
    t15 = <motion.div className="1.5xl:min-w-[630px] 1.5xl:max-w-[700px] relative flex flex-col gap-6 lg:w-[46%]" initial={t0} animate={t1} transition={t2}><div className="relative flex flex-col items-center gap-6 lg:items-start">{t10}{t12}{t14}</div></motion.div>;
    $[31] = t10;
    $[32] = t12;
    $[33] = t14;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  return t15;
}
function RightSideHeroContent() {
  const $ = _c(5);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      opacity: 0,
      y: 28
    };
    t1 = {
      opacity: 1,
      y: 0
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      duration: 1.3,
      ease: [0.22, 1, 0.36, 1],
      delay: 0.35
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <HeroCalculator />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <motion.div className="relative z-interactive" initial={t0} animate={t1} transition={t2}><div className="relative z-interactive lg:flex">{t3}<div className="pointer-events-none absolute -right-15 bottom-[-10] z-interactive hidden h-[550px] w-[159px] lg:block"><Image alt="Zack" src={HeroSectionZack} fill={true} className="object-contain" preload={true} sizes="(max-width: 1023px) 1px, 159px" /></div></div></motion.div>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
}
function BackgroundDecoration() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <><motion.div className="pointer-events-none absolute top-0 left-0 -z-10 h-full w-full translate-x-1/2 -translate-y-1/2 scale-200 bg-contain bg-bottom bg-no-repeat opacity-70 md:translate-y-0 md:scale-150 lg:top-2/3 lg:left-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2" style={{
        backgroundImage: `url(${HeroSectionBackground.src})`
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 1.5,
        ease: "easeOut"
      }} /><div className="pointer-events-none absolute top-2/5 left-1/6 -z-10 h-[200px] w-[250px] bg-[#903BFF] blur-[200px]" /><div className="pointer-events-none absolute top-2/3 right-0 -z-10 hidden h-[200px] w-[200px] transform-gpu will-change-transform before:absolute before:inset-0 before:bg-[#73EAFF] before:blur-[200px] before:content-[''] md:block" /></>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function HeroSection() {
  const $ = _c(16);
  const {
    headerHeight
  } = useLayoutContext();
  const isClient = useIsClient();
  const matches1440 = useMediaQuery("(min-width: 1440px)");
  let t0;
  if ($[0] !== headerHeight) {
    t0 = {
      marginTop: headerHeight
    };
    $[0] = headerHeight;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== headerHeight || $[3] !== isClient || $[4] !== matches1440) {
    t1 = isClient && {
      style: {
        top: headerHeight * -1 - (matches1440 ? 7 : 17),
        transform: "translate3d(0, 0, 0)"
      }
    };
    $[2] = headerHeight;
    $[3] = isClient;
    $[4] = matches1440;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      hidden: {
        opacity: 0
      },
      visible: {
        opacity: 1
      }
    };
    t3 = {
      duration: 1,
      delay: 0.5
    };
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t1) {
    t4 = <motion.div {...t1} initial="hidden" animate="visible" exit="hidden" variants={t2} transition={t3} />;
    $[8] = t1;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      hidden: {
        opacity: 0
      },
      visible: {
        opacity: 1
      }
    };
    t6 = {
      duration: 1
    };
    $[10] = t5;
    $[11] = t6;
  } else {
    t5 = $[10];
    t6 = $[11];
  }
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <motion.div className="flex w-full flex-col items-center gap-4" initial="hidden" animate="visible" exit="hidden" variants={t5} transition={t6}><HeroSectionContainer className="relative w-full overflow-hidden" data-name="Container"><HeroContentContainer className="1.5xl:px-20 3xl:px-24 1.5xl:gap-[122px] relative z-interactive mx-auto flex flex-col justify-center gap-6 overflow-hidden px-4 pt-[43px] pb-[76px] md:gap-8 md:px-8 lg:flex-row lg:px-12"><LeftSideHeroContent /><RightSideHeroContent /><BackgroundDecoration /></HeroContentContainer><TrustStrip /></HeroSectionContainer></motion.div>;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t0 || $[14] !== t4) {
    t8 = <section className="relative flex flex-1 flex-col items-center justify-center" style={t0}>{t4}{t7}</section>;
    $[13] = t0;
    $[14] = t4;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
}
export default HeroSection;